<template>
  <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
    <header class="mb-8 text-center">
      <h1 class="text-3xl sm:text-4xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
        Conversor de <span class="bg-clip-text text-transparent bg-gradient-to-r from-[#7B2CBF] to-[#A367E1]">Moedas</span>
      </h1>
      <p class="text-base text-slate-500 dark:text-slate-400 mt-2">
        Converta valores, salve favoritos e acompanhe a variação do câmbio.
      </p>
    </header>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
      <div class="panel space-y-6">
        <div class="flex flex-wrap gap-2">
          <div v-for="p in favoritePairs" :key="p.key" class="fav-pill group">
            <button @click="applyPair(p.from, p.to)" class="inline-flex items-center gap-1.5 px-2" title="Aplicar par favorito">
              <span class="material-symbols-outlined text-base text-[#5A189A] dark:text-[#D9BFFB]">star</span>{{ p.from }}→{{ p.to }}
            </button>
            <button @click.stop="removeFavorite(p.key)" class="ml-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Excluir favorito">
              <span class="material-symbols-outlined text-sm">close</span>
            </button>
          </div>
        </div>

        <div>
          <label for="amount" class="form-label">Valor a converter</label>
          <input id="amount" type="number" v-model.number="amount" class="form-input text-xl" min="0" step="0.01" placeholder="1,00" />
        </div>

        <div class="flex items-center gap-3">
          <div class="flex-1">
            <label for="from" class="form-label">De</label>
            <div class="relative">
              <select id="from" v-model="fromCurrency" class="form-select">
                <optgroup label="Mais usadas">
                  <option v-for="c in commonCodes" :key="'c1' + c" :value="c">{{ c }} - {{ currencies[c] || c }}</option>
                </optgroup>
                <optgroup label="Todas">
                  <option v-for="(name, code) in currencies" :key="'c2' + code" :value="code">{{ code }} - {{ name }}</option>
                </optgroup>
              </select>
              <span class="select-arrow material-symbols-outlined">expand_more</span>
            </div>
          </div>

          <button @click="swapCurrencies" title="Inverter moedas" class="btn-icon mt-8">
            <span class="material-symbols-outlined transition-transform duration-300" :class="{ 'rotate-180': isSwapping }">swap_horiz</span>
          </button>

          <div class="flex-1">
            <label for="to" class="form-label">Para</label>
            <div class="relative">
              <select id="to" v-model="toCurrency" class="form-select">
                <optgroup label="Mais usadas">
                  <option v-for="c in commonCodes" :key="'c3' + c" :value="c">{{ c }} - {{ currencies[c] || c }}</option>
                </optgroup>
                <optgroup label="Todas">
                  <option v-for="(name, code) in currencies" :key="'c4' + code" :value="code">{{ code }} - {{ name }}</option>
                </optgroup>
              </select>
              <span class="select-arrow material-symbols-outlined">expand_more</span>
            </div>
          </div>
        </div>

        <div class="border-t border-slate-200/80 dark:border-slate-700/60 pt-6 flex flex-col sm:flex-row gap-3">
          <button @click="toggleFavoriteCurrentPair" class="btn btn-secondary flex-1" title="Salvar par de moedas atual como favorito">
            <span class="material-symbols-outlined text-lg" :class="{ 'fill-1 text-amber-400': isCurrentPairFavorite }">{{ isCurrentPairFavorite ? 'star' : 'star' }}</span>
            <span>{{ isCurrentPairFavorite ? 'Remover Favorito' : 'Salvar Par' }}</span>
          </button>
          <button @click="convertCurrency" :disabled="isLoading" class="btn btn-primary flex-1">
            <span v-if="isLoading" class="loader"></span>
            <span v-else class="flex items-center">
              <span class="material-symbols-outlined mr-2">currency_exchange</span>
              Converter
            </span>
          </button>
        </div>

        <p v-if="quoteMeta" class="text-xs text-slate-500 dark:text-slate-400 text-center">
          Cotação de <strong>{{ quoteMeta.date }}</strong>
          <span v-if="quoteMeta.fromCache" class="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 text-[10px]">
            <span class="material-symbols-outlined text-xs">cloud_off</span> offline (cache)
          </span>
        </p>
      </div>

      <div class="space-y-6">
        <div class="panel flex flex-col justify-center min-h-[260px]">
          <transition name="result-fade" mode="out-in">
            <div v-if="convertedAmount !== null" key="result">
              <p class="text-base text-slate-600 dark:text-slate-400 font-medium">
                {{ formatCurrency(amount, 2) }} {{ fromCurrency }} equivale a:
              </p>
              <p class="text-5xl lg:text-6xl font-extrabold text-[#3C096C] dark:text-[#D9BFFB] break-words tracking-tight">
                {{ formatCurrency(convertedAmount) }}
              </p>
              <p class="text-2xl font-medium text-slate-500 dark:text-slate-300 mt-1">{{ toCurrency }}</p>
              <div v-if="exchangeRate" class="mt-4 text-sm text-slate-500 dark:text-slate-400 border-t border-slate-200/80 dark:border-slate-700/60 pt-3">
                <p class="font-semibold">1 {{ fromCurrency }} = {{ formatCurrency(exchangeRate, 6) }} {{ toCurrency }}</p>
              </div>
            </div>

            <div v-else-if="error" key="error" class="text-center text-rose-500 dark:text-rose-400">
              <span class="material-symbols-outlined text-6xl">error</span>
              <p class="mt-2 text-xl font-semibold">Ocorreu um erro</p>
              <p class="text-base mt-1">{{ error }}</p>
            </div>

            <div v-else key="placeholder" class="text-center text-slate-400 dark:text-slate-500">
              <span class="material-symbols-outlined text-7xl">request_quote</span>
              <p class="mt-2 text-xl font-semibold">O resultado aparecerá aqui.</p>
            </div>
          </transition>
        </div>

        <div class="panel">
          <div class="flex items-center justify-between mb-3">
            <h3 class="panel-title">Variação (30 dias): {{ fromCurrency }} → {{ toCurrency }}</h3>
            <span v-if="series.loading" class="text-xs text-slate-500">carregando…</span>
          </div>

          <div class="h-40">
            <div v-if="series.error" class="empty-state">Sem dados para o gráfico.</div>
            <div v-else ref="chartWrap" class="w-full h-full">
              <svg v-if="series.points.length" :viewBox="`0 0 ${dims.w} ${dims.h}`" class="w-full h-full">
                <defs>
                  <linearGradient :id="areaId" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#7B2CBF" stop-opacity="0.25" />
                    <stop offset="100%" stop-color="#7B2CBF" stop-opacity="0" />
                  </linearGradient>
                  <filter :id="shadowId" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="8" stdDeviation="8" flood-color="#7B2CBF" flood-opacity="0.25" />
                  </filter>
                </defs>

                <g>
                  <path :d="areaPath" :fill="`url(#${areaId})`" />
                  <path :d="linePath" stroke="currentColor" stroke-width="3" fill="none" :filter="`url(#${shadowId})`" class="text-[#7B2CBF] dark:text-[#D9BFFB]" stroke-linecap="round" stroke-linejoin="round" />
                  <circle :cx="lastPoint.x" :cy="lastPoint.y" r="4" class="text-[#7B2CBF] dark:text-[#D9BFFB]" fill="currentColor" />
                </g>

                <g v-if="series.min !== null" class="text-[10px] fill-slate-500 dark:fill-slate-400">
                  <text :x="8" :y="12">{{ formatCurrency(series.max, 4) }}</text>
                  <text :x="8" :y="dims.h - 4">{{ formatCurrency(series.min, 4) }}</text>
                </g>
              </svg>
              <div v-else class="empty-state">Sem dados para o gráfico.</div>
            </div>
          </div>

          <p v-if="series.points.length && !series.loading" class="mt-2 text-xs text-center text-slate-500 dark:text-slate-400">
            mín {{ formatCurrency(series.min, 4) }} • máx {{ formatCurrency(series.max, 4) }}
          </p>
        </div>

        <div class="panel h-[300px] flex flex-col">
          <div class="flex items-center justify-between mb-3">
            <h3 class="panel-title">Histórico</h3>
            <button @click="clearHistory" :disabled="!history.length" class="text-xs px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-700/70 dark:hover:bg-slate-700 disabled:opacity-50 transition">
              Limpar
            </button>
          </div>
          <ul v-if="history.length" class="divide-y divide-slate-200/80 dark:divide-slate-700/60 overflow-y-auto flex-1 -mr-2 pr-2">
            <li v-for="(h, i) in history" :key="h.id" class="py-3 flex items-center justify-between">
              <button @click="reRunHistory(h)" class="text-left hover:opacity-80">
                <p class="text-sm">
                  <span class="font-semibold">{{ h.amountFormatted }}</span>
                  {{ h.from }} → <span class="font-semibold text-[#3C096C] dark:text-[#D9BFFB]">{{ h.resultFormatted }}</span> {{ h.to }}
                </p>
                <p class="text-xs text-slate-500">{{ h.date }} • taxa: {{ h.rateFormatted }}</p>
              </button>
              <button @click="removeHistory(i)" class="btn-icon-sm text-slate-400 hover:text-rose-500">
                <span class="material-symbols-outlined text-base">delete</span>
              </button>
            </li>
          </ul>
          <div v-else class="empty-state h-full">Sem conversões recentes.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import axios from 'axios'

const amount = ref(1)
const fromCurrency = ref('BRL')
const toCurrency = ref('USD')
const currencies = ref({})
const commonCodes = ['BRL', 'USD', 'EUR', 'GBP', 'ARS', 'CLP', 'CAD', 'AUD', 'JPY', 'CNY']

const isLoading = ref(false)
const convertedAmount = ref(null)
const error = ref(null)
const isSwapping = ref(false)

const API_BASE_URL = 'https://api.frankfurter.dev/v2'

const quoteMeta = ref(null)

const favoritePairs = ref(loadLS('ce_fav_pairs', [
  { key: 'BRL-USD', from: 'BRL', to: 'USD' },
  { key: 'BRL-EUR', from: 'BRL', to: 'EUR' },
  { key: 'USD-BRL', from: 'USD', to: 'BRL' }
]))

const isCurrentPairFavorite = computed(() =>
  !!favoritePairs.value.find(p => p.from === fromCurrency.value && p.to === toCurrency.value)
)

function toggleFavoriteCurrentPair() {
  const key = `${fromCurrency.value}-${toCurrency.value}`
  const idx = favoritePairs.value.findIndex(p => p.key === key)

  if (idx >= 0) {
    favoritePairs.value.splice(idx, 1)
  } else {
    favoritePairs.value.unshift({ key, from: fromCurrency.value, to: toCurrency.value })
  }

  saveLS('ce_fav_pairs', favoritePairs.value)
}

function removeFavorite(key) {
  const idx = favoritePairs.value.findIndex(p => p.key === key)

  if (idx >= 0) {
    favoritePairs.value.splice(idx, 1)
    saveLS('ce_fav_pairs', favoritePairs.value)
  }
}

function applyPair(from, to) {
  fromCurrency.value = from
  toCurrency.value = to
  convertCurrency()
}

const history = ref(loadLS('ce_conv_history', []))

function pushHistory({ amount, from, to, rate, result, date }) {
  const item = {
    id: Date.now() + Math.random().toString(16).slice(2),
    amount,
    from,
    to,
    rate,
    result,
    date,
    amountFormatted: formatCurrency(amount, 2),
    resultFormatted: formatCurrency(result),
    rateFormatted: formatCurrency(rate, 6)
  }

  history.value.unshift(item)
  history.value = history.value.slice(0, 12)
  saveLS('ce_conv_history', history.value)
}

function reRunHistory(h) {
  amount.value = h.amount
  fromCurrency.value = h.from
  toCurrency.value = h.to
  convertCurrency()
}

function removeHistory(i) {
  history.value.splice(i, 1)
  saveLS('ce_conv_history', history.value)
}

function clearHistory() {
  history.value = []
  saveLS('ce_conv_history', history.value)
}

const series = ref({ loading: false, points: [], min: null, max: null, error: null })
const chartWrap = ref(null)
const dims = ref({ w: 600, h: 160, pad: { top: 14, right: 10, bottom: 18, left: 8 } })
const areaId = `grad-${Math.random().toString(36).slice(2)}`
const shadowId = `shad-${Math.random().toString(36).slice(2)}`
let resizeObserver = null

const inner = computed(() => {
  const { w, h, pad } = dims.value
  return { iw: w - pad.left - pad.right, ih: h - pad.top - pad.bottom }
})

const scale = computed(() => {
  const n = Math.max(series.value.points.length, 2)
  const iw = inner.value.iw
  const step = iw / (n - 1)
  const min = series.value.min ?? 0
  const max = series.value.max ?? 1
  const padY = (max - min) * 0.1 || 0.1
  const ymin = min - padY
  const ymax = max + padY

  return { step, ymin, ymax }
})

const pointsXY = computed(() => {
  if (!series.value.points.length) return []

  const { pad } = dims.value
  const { ih } = inner.value
  const { step, ymin, ymax } = scale.value
  const rng = ymax - ymin || 1

  return series.value.points.map((v, i) => {
    const x = pad.left + i * step
    const y = pad.top + (ih - ((v - ymin) / rng) * ih)

    return { x, y }
  })
})

const linePath = computed(() => {
  const pts = pointsXY.value

  if (!pts.length) return ''
  if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`

  let d = `M ${pts[0].x} ${pts[0].y}`

  for (let i = 1; i < pts.length; i++) {
    const p0 = pts[i - 1]
    const p1 = pts[i]
    const cx = (p0.x + p1.x) / 2
    d += ` C ${cx} ${p0.y}, ${cx} ${p1.y}, ${p1.x} ${p1.y}`
  }

  return d
})

const areaPath = computed(() => {
  const pts = pointsXY.value

  if (!pts.length) return ''

  const { h, pad } = dims.value
  const baseline = h - pad.bottom

  let d = `M ${pts[0].x} ${baseline} L ${pts[0].x} ${pts[0].y}`

  if (pts.length === 1) {
    d += ` L ${pts[0].x} ${baseline}`
  } else {
    for (let i = 1; i < pts.length; i++) {
      const p0 = pts[i - 1]
      const p1 = pts[i]
      const cx = (p0.x + p1.x) / 2
      d += ` C ${cx} ${p0.y}, ${cx} ${p1.y}, ${p1.x} ${p1.y}`
    }

    d += ` L ${pts[pts.length - 1].x} ${baseline}`
  }

  d += ' Z'

  return d
})

const lastPoint = computed(() => {
  const pts = pointsXY.value
  return pts.length ? pts[pts.length - 1] : { x: 0, y: 0 }
})

const exchangeRate = computed(() =>
  convertedAmount.value !== null && amount.value > 0
    ? convertedAmount.value / amount.value
    : null
)

function loadLS(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback
  } catch {
    return fallback
  }
}

function saveLS(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val))
  } catch {
    // ignore
  }
}

async function fetchCurrencies() {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/currencies`)

    const list = Array.isArray(data)
      ? data
      : Object.entries(data).map(([iso_code, name]) => ({ iso_code, name }))

    currencies.value = Object.fromEntries(
      list
        .filter(c => c.iso_code && c.name)
        .sort((a, b) => a.iso_code.localeCompare(b.iso_code))
        .map(c => [c.iso_code, c.name])
    )
  } catch {
    error.value = 'Não foi possível carregar as moedas.'
  }
}

async function convertCurrency() {
  if (!amount.value || amount.value <= 0) {
    error.value = 'Informe um valor válido.'
    return
  }

  error.value = null

  if (fromCurrency.value === toCurrency.value) {
    convertedAmount.value = amount.value
    quoteMeta.value = { date: new Date().toISOString().slice(0, 10), fromCache: false }
    series.value = { loading: false, points: [], min: null, max: null, error: null }
    return
  }

  isLoading.value = true

  const cacheKey = `ce_rate_${fromCurrency.value}_${toCurrency.value}`

  try {
    const { data } = await axios.get(`${API_BASE_URL}/rate/${fromCurrency.value}/${toCurrency.value}`)

    const unitRate = data.rate
    const result = amount.value * unitRate

    convertedAmount.value = result
    quoteMeta.value = { date: data.date, fromCache: false }

    saveLS(cacheKey, { date: data.date, rate: unitRate })

    pushHistory({
      amount: amount.value,
      from: fromCurrency.value,
      to: toCurrency.value,
      rate: unitRate,
      result,
      date: data.date
    })

    fetchSeries()
  } catch {
    const cached = loadLS(cacheKey, null)

    if (cached) {
      const result = cached.rate * amount.value

      convertedAmount.value = result
      quoteMeta.value = { date: cached.date, fromCache: true }

      pushHistory({
        amount: amount.value,
        from: fromCurrency.value,
        to: toCurrency.value,
        rate: cached.rate,
        result,
        date: cached.date
      })

      fetchSeries()
    } else {
      error.value = 'Erro ao converter. Tente novamente.'
      convertedAmount.value = null
      series.value = { loading: false, points: [], min: null, max: null, error: null }
    }
  } finally {
    isLoading.value = false
  }
}

async function fetchSeries() {
  series.value = { loading: true, points: [], min: null, max: null, error: null }

  if (fromCurrency.value === toCurrency.value) {
    series.value = { loading: false, points: [], min: null, max: null, error: null }
    return
  }

  try {
    const end = new Date()
    const start = new Date()

    start.setDate(end.getDate() - 30)

    const fmt = d => d.toISOString().slice(0, 10)

    const { data } = await axios.get(`${API_BASE_URL}/rates`, {
      params: {
        from: fmt(start),
        to: fmt(end),
        base: fromCurrency.value,
        quotes: toCurrency.value
      }
    })

    const points = data
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(item => item.rate)
      .filter(v => typeof v === 'number')

    if (!points.length) {
      series.value = { loading: false, points: [], min: null, max: null, error: 'Sem dados para este par.' }
      return
    }

    const min = Math.min(...points)
    const max = Math.max(...points)

    series.value = { loading: false, points, min, max, error: null }
  } catch {
    series.value = { loading: false, points: [], min: null, max: null, error: 'Não foi possível carregar a série.' }
  }
}

function swapCurrencies() {
  isSwapping.value = true

  const oldFrom = fromCurrency.value
  fromCurrency.value = toCurrency.value
  toCurrency.value = oldFrom

  setTimeout(() => {
    isSwapping.value = false
  }, 300)

  convertedAmount.value = null
  series.value = { loading: false, points: [], min: null, max: null, error: null }
  quoteMeta.value = null
}

function formatCurrency(value, maxDecimals = 4) {
  if (typeof value !== 'number' || isNaN(value)) return ''

  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: maxDecimals
  }).format(value)
}

watch([fromCurrency, toCurrency], () => {
  convertedAmount.value = null
  quoteMeta.value = null
  series.value = { loading: false, points: [], min: null, max: null, error: null }
})

onMounted(async () => {
  await fetchCurrencies()
  observeChart()
})

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})

function observeChart() {
  resizeObserver = new ResizeObserver((entries) => {
    for (const e of entries) {
      const cr = e.contentRect
      const w = Math.max(320, Math.floor(cr.width))
      const h = Math.max(140, Math.floor(cr.height))

      dims.value = { w, h, pad: { top: 14, right: 10, bottom: 18, left: 8 } }
    }
  })

  if (chartWrap.value) {
    resizeObserver.observe(chartWrap.value)
  }
}
</script>

<style scoped>
.panel {
  @apply bg-white/70 dark:bg-[#240046]/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-3xl shadow-xl shadow-slate-200/30 dark:shadow-black/20 p-6;
}
.form-label {
  @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2;
}
.form-input {
  @apply w-full px-4 py-3 text-base text-slate-800 dark:text-slate-100 bg-slate-100/80 dark:bg-slate-900/50 rounded-xl transition ease-in-out ring-1 ring-transparent focus:outline-none focus:ring-2 focus:ring-[#5A189A] placeholder:text-slate-400 dark:placeholder:text-slate-500;
}
.form-select {
  @apply appearance-none block w-full px-4 py-3 text-base font-normal text-slate-800 dark:text-slate-100 bg-slate-100/80 dark:bg-slate-900/50 rounded-xl transition ease-in-out ring-1 ring-transparent focus:outline-none focus:ring-2 focus:ring-[#5A189A] cursor-pointer;
}
.select-arrow {
  @apply pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 dark:text-slate-400;
}
.btn {
  @apply inline-flex items-center justify-center gap-2 py-3 px-5 rounded-xl text-base font-semibold transition-all duration-200 active:scale-95 disabled:opacity-60;
}
.btn-primary {
  @apply text-white bg-[#5A189A] hover:bg-[#3C096C] shadow-lg shadow-[#5A189A]/20;
}
.btn-secondary {
  @apply text-slate-700 dark:text-slate-200 bg-white/70 dark:bg-slate-700/50 ring-1 ring-slate-200 dark:ring-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700;
}
.btn-icon {
  @apply p-3 text-slate-500 hover:text-[#5A189A] dark:text-slate-400 dark:hover:text-[#D9BFFB] bg-slate-100/80 hover:bg-[#5A189A]/10 dark:bg-slate-700/70 dark:hover:bg-slate-700 rounded-full transition-all duration-200 ease-in-out transform active:scale-90;
}
.btn-icon-sm {
  @apply h-8 w-8 grid place-items-center rounded-full transition text-slate-500 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-600 active:scale-90;
}
.fav-pill {
  @apply inline-flex items-center text-xs font-medium pl-1 pr-1 py-1 rounded-full transition-colors bg-[#5A189A]/10 text-[#3C096C] dark:bg-[#7B2CBF]/20 dark:text-[#D9BFFB] ring-1 ring-[#5A189A]/20 dark:ring-[#7B2CBF]/30;
}
.empty-state {
  @apply h-full flex flex-col items-center justify-center text-sm text-center text-slate-500 dark:text-slate-400;
}
.loader {
  width: 24px;
  height: 24px;
  border: 3px solid #FFF;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0) }
  to { transform: rotate(360deg) }
}
.result-fade-enter-active,
.result-fade-leave-active {
  transition: opacity .3s ease, transform .3s ease;
}
.result-fade-enter-from,
.result-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
.rotate-180 {
  transform: rotate(180deg);
}
.panel-title {
  @apply text-sm font-semibold text-slate-700 dark:text-slate-200;
}
</style>